import { useRef } from "react";

import { RISK_MODEL_PREDICTIONS_GENERIC_FILTER } from "shared/filterDefinitions";
import { useInspectionItemsSchema } from "shared/schemas/inspectionItemsSchema";
import useManufactureLineExitsSchema from "shared/schemas/manufactureLineExitsSchema";
import useRepairLaborSchema from "shared/schemas/repairLaborSchema";
import useRepairPartsSchema from "shared/schemas/repairPartsSchema";
import useRepairsSchema, {
  useRepairsSchemaV2,
} from "shared/schemas/repairsSchema";
import { useRiskModelPredictionsSchema } from "shared/schemas/riskModelPredictionsSchema";
import useVehicleECUsSchema from "shared/schemas/vehicleECUsSchema";
import useVehicleOptionsSchema from "shared/schemas/vehicleOptionsSchema";
import useVehiclePartsSchema from "shared/schemas/vehiclePartsSchema";
import useWorkOrderPartsSchema from "shared/schemas/workOrderPartsSchema";

import { SchemaEntry } from "features/ui/MaterialTable";

const schemasAreEqual = (
  a: Record<string, SchemaEntry[]>,
  b: Record<string, SchemaEntry[]>
): boolean => {
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;

  return keys.every(
    (key) =>
      a[key].length === b[key]?.length &&
      a[key].every((entry, i) => entry.accessor === b[key][i]?.accessor)
  );
};

export const useRelationshipSchemaMap = (): Record<string, SchemaEntry[]> => {
  const manufactureLineExitsSchema = useManufactureLineExitsSchema(["VIN"]);
  const vehicleECUsSchema = useVehicleECUsSchema(["VIN"]);
  const inspectionItemsSchema = useInspectionItemsSchema(["VIN"]);
  const riskModelPredictionsSchema = useRiskModelPredictionsSchema(
    RISK_MODEL_PREDICTIONS_GENERIC_FILTER,
    ["failureMode", "VIN"]
  );
  const vehicleOptionsSchema = useVehicleOptionsSchema(["VIN"]);
  const vehiclePartsSchema = useVehiclePartsSchema();
  const workOrderPartsSchema = useWorkOrderPartsSchema();
  const repairsSchema = useRepairsSchema();
  const repairsSchemaV2 = useRepairsSchemaV2();
  const repairPartsSchema = useRepairPartsSchema();
  const repairLaborSchema = useRepairLaborSchema();

  const map: Record<string, SchemaEntry[]> = {
    "/api/v1/vehicles/manufactureLineExits": manufactureLineExitsSchema.schema,
    "/api/v1/vehicleECUs": vehicleECUsSchema.schema,
    "/api/v1/inspections/items": inspectionItemsSchema.schema,
    "/api/v1/riskModelPredictions": riskModelPredictionsSchema.schema,
    "/api/v1/vehicleOptions": vehicleOptionsSchema.schema,
    "/api/v1/vehicleParts": vehiclePartsSchema.schema,
    "/api/v1/workOrderParts": workOrderPartsSchema.schema,
    "/api/v1/repairs": repairsSchema.schema,
    "/api/v2/workOrders/repairs": repairsSchemaV2.schema,
    "/api/v2/workOrders/repairParts": repairPartsSchema.schema,
    "/api/v2/workOrders/repairLabor": repairLaborSchema.schema,
  };

  // the individual schema hooks return new arrays on every render (they don't
  // memoize internally). Return a stable reference when the schema content
  // hasn't actually changed to prevent unnecessary downstream recomputation.
  const prevRef = useRef(map);
  if (!schemasAreEqual(prevRef.current, map)) {
    prevRef.current = map;
  }

  return prevRef.current;
};

export const useRelationshipFilterSchema = (
  relationshipEndpoint: string
): SchemaEntry[] => {
  const schemaMap = useRelationshipSchemaMap();

  return schemaMap[relationshipEndpoint] || [];
};
